// src/pages/ServiceDetailPage.jsx

import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Divider,
  Card,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Skeleton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";

// SEO & Data
import SEO from "../components/SEO";
import { SERVICES_SEO_MAP } from "../data/servicesSeoData";

// Firestore
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sameCategoryServices, setSameCategoryServices] = useState([]);
  const [moreServices, setMoreServices] = useState([]);
  const [moreLoading, setMoreLoading] = useState(false);

  useEffect(() => {
    if (!serviceId) {
      setLoading(false);
      setError("No service ID provided.");
      return;
    }

    const fetchServiceDetails = async () => {
      try {
        let main = null;

        // 1. Try fetching by Firestore doc ID
        try {
          const docRef = doc(db, "services", serviceId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            main = { id: docSnap.id, ...docSnap.data() };
          }
        } catch (e) {
          // Continue to slug resolution
        }

        // 2. Try querying Firestore by slugId
        if (!main) {
          try {
            const qSlug = query(
              collection(db, "services"),
              where("slugId", "==", serviceId),
              limit(1)
            );
            const slugSnap = await getDocs(qSlug);
            if (!slugSnap.empty) {
              main = { id: slugSnap.docs[0].id, ...slugSnap.docs[0].data() };
            }
          } catch (e) {
            // Continue to static fallback
          }
        }

        // 3. Fallback to predefined core studio services in SERVICES_SEO_MAP
        if (!main && SERVICES_SEO_MAP[serviceId]) {
          main = SERVICES_SEO_MAP[serviceId];
        }

        if (main) {
          const staticSeo = SERVICES_SEO_MAP[main.slugId || serviceId] || {};
          const enriched = {
            ...staticSeo,
            ...main,
            title: main.title || staticSeo.title,
            seoTitle: staticSeo.seoTitle || `${main.title} in Jaipur | WiYou! Tech Studios`,
            metaDescription: staticSeo.metaDescription || main.description || main.subtitle,
            features: main.features || staticSeo.features || [],
            deliverables: main.deliverables || staticSeo.deliverables || [],
            faqs: staticSeo.faqs || main.faqs || [],
          };
          setService(enriched);
          await fetchRelatedServices(enriched);
        } else {
          setError(`No service found with ID: ${serviceId}.`);
        }
      } catch (err) {
        console.error("Error fetching service details:", err);
        setError("Unable to load service details.");
      } finally {
        setLoading(false);
      }
    };

    const fetchRelatedServices = async (mainService) => {
      setMoreLoading(true);
      try {
        const servicesRef = collection(db, "services");

        // 1) More from same category (excluding current)
        let sameCategory = [];
        if (mainService.category) {
          const sameQ = query(
            servicesRef,
            where("category", "==", mainService.category),
            limit(6)
          );
          const sameSnap = await getDocs(sameQ);
          sameCategory = sameSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((s) => s.id !== mainService.id);
        }
        setSameCategoryServices(sameCategory);

        // 2) More from us (excluding current + same-category)
        const moreQ = query(servicesRef, limit(20));
        const moreSnap = await getDocs(moreQ);
        const all = moreSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const sameIds = new Set([
          mainService.id,
          ...sameCategory.map((s) => s.id),
        ]);

        const others = all.filter(
          (s) =>
            !sameIds.has(s.id) &&
            s.id !== mainService.id &&
            s.category !== mainService.category
        );

        setMoreServices(others);
      } catch (err) {
        console.error("Error fetching related services:", err);
      } finally {
        setMoreLoading(false);
      }
    };

    fetchServiceDetails();
  }, [serviceId]);

  const titleFallback =
    serviceId?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "Service";

  // ---------- LOADING ---------- //
  if (loading) {
    return (
      <Container sx={{ pt: 12, pb: 8 }}>
        <Skeleton variant="text" width="40%" height={50} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  // ---------- ERROR ---------- //
  if (error || !service) {
    return (
      <Container sx={{ pt: 12, pb: 8 }}>
        <Alert severity="error">{error || "Failed to load service."}</Alert>
        <Button
          sx={{ mt: 3 }}
          component={RouterLink}
          to="/services"
          startIcon={<ArrowBackIosNewIcon />}
        >
          Back to Services
        </Button>
      </Container>
    );
  }

  // ---------- PRICING LOGIC ---------- //
  const amount = Number(service.amount) || null; // original price
  const price = Number(service.price) || null; // discounted price
  const discount =
    amount && price ? Math.round(((amount - price) / amount) * 100) : null;

  const shortDescription =
    service.description || service.longDescription || "No short description.";

  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://studio.wiyoutech.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://studio.wiyoutech.in/services"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": service.title || titleFallback,
            "item": `https://studio.wiyoutech.in/services/${service.slugId || serviceId}`
          }
        ]
      },
      {
        "@type": "Service",
        "@id": `https://studio.wiyoutech.in/services/${service.slugId || serviceId}#service`,
        "name": service.title || titleFallback,
        "description": service.metaDescription || service.description || service.about || "",
        "provider": {
          "@type": "ProfessionalService",
          "name": "WiYou! Tech Studios",
          "url": "https://studio.wiyoutech.in/",
          "telephone": "+91-9119119119",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Jaipur",
            "addressRegion": "Rajasthan",
            "addressCountry": "IN"
          }
        },
        "areaServed": [
          { "@type": "City", "name": "Jaipur" },
          { "@type": "State", "name": "Rajasthan" },
          { "@type": "Country", "name": "India" }
        ],
        ...(price ? { "offers": { "@type": "Offer", "price": price, "priceCurrency": "INR" } } : {})
      },
      ...(Array.isArray(service.faqs) && service.faqs.length > 0 ? [{
        "@type": "FAQPage",
        "mainEntity": service.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      }] : [])
    ]
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: { xs: 9, md: 13 },
        pb: 10,
        background:
          "#F7F4EF00",
      }}
    >
      <SEO
        title={service.seoTitle || `${service.title} in Jaipur | WiYou! Tech Studios`}
        description={service.metaDescription || service.description || service.about || ""}
        keywords={service.keywords || [service.title, "web development company Jaipur", "WiYou! Tech Studios"]}
        canonicalUrl={`https://studio.wiyoutech.in/services/${service.slugId || serviceId}`}
        ogImage={service.imageUrl || "https://studio.wiyoutech.in/wiyou-logo.png"}
        schema={pageSchema}
      />
      <Container maxWidth="lg">
        {/* Top bar */}
        <Box mb={3} p={0.5} display="flex" justifyContent="space-between" gap={2} flexWrap={"wrap"}>
          <Button
            component={RouterLink}
            to="/services"
            startIcon={<ArrowBackIosNewIcon />}
            sx={{ textTransform: "none" }}
          >
            Back to All Services
          </Button>

          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}
            separator={<ArrowForwardIosIcon sx={{ fontSize: { xs: 10, md: 14 } }} />}
          >
            <Link component={RouterLink} to="/" underline="hover" sx={{ fontSize: { xs: 12, md: 14 } }}>
              Home
            </Link>
            <Link component={RouterLink} to="/services" underline="hover" sx={{ fontSize: { xs: 12, md: 14 } }}>
              Services
            </Link>
            <Typography color="text.primary" sx={{ fontSize: { xs: 12, md: 14 } }}>
              {service.title || titleFallback}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Title + category + description */}
        <Box mb={3}>
          <Typography
            variant="h3"
            component="h1"
            fontFamily="Georgia, Times New Roman, serif"
            sx={{
              letterSpacing: 0.6,
              mb: 1,
              fontSize: { xs: "2.35rem", sm: "3rem", md: "4.1rem" },
              lineHeight: 1.02,
              fontWeight: 800,
            }}
          >
            {service.title || titleFallback}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
            {service.category && (
              <Chip
                icon={<CategoryRoundedIcon sx={{ fontSize: 18 }} />}
                label={service.category}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 700 }}
          >
            {shortDescription}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Main layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.9fr) minmax(0, 1.1fr)" },
            gap: { xs: 3, md: 7 },
            alignItems: "flex-start",
          }}
        >
          {/* LEFT: sticky image column */}
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: { xs: "auto", md: 96 }, // adjust to your header height
              alignSelf: "flex-start",
            }}
          >
                {discount !== null && (
                  <Chip
                    label={`You Got ${discount}% OFF`}
                    sx={{
                        position: "relative",
                        top: { xs: 40, md: 20 },
                        left: { xs: 245, md: 415 },
                        bgcolor: "#0048ff48",
                        color: "#000000ff",
                        fontWeight: "bold",
                        backdropFilter: "blur(30px)",
                    }}
                  />
                )}
            <Card
              elevation={0}
              sx={{
                borderRadius: "24px 24px 12px 12px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "#DCD4CC",
              }}
            >
              {service.imageUrl ? (
                <Box
                  alt={service.title || titleFallback}
                  sx={{
                    width: "100%",
                    height: { xs: 410, md: 540 },
                    backgroundImage: `url(${service.imageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.200",
                  }}
                >
                  <Typography>No Image Provided</Typography>
                </Box>
              )}
            </Card>

                        {/* PRICING */}
<Card
  elevation={0}
  sx={{
    px: 3,
    py: 1,
    background: "#F0EBE4",
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    alignItems: "center",  justifyContent: "space-between",
    gap: 2,
    borderRadius: "8px 8px 20px 20px",
    position: "relative",
    mt: -5,
    backdropFilter: "blur(30px)",
  }}
>
<Box
    sx={{ display: "flex", flexDirection: "column" }}
>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      Price:
    </Typography>
  <Stack direction="row" alignItems={"center"} spacing={2} mb={2}>

    {price && (
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: "bold",
          color: "black",
        }}
      >
        ₹{price}
      </Typography>
    )}

    {amount && (
      <Typography
        sx={{
          textDecoration: "line-through",
          color: "grey.500",
          fontSize: "1.1rem",
        }}
      >
        ₹{amount}
      </Typography>
    )}

  </Stack>
</Box>
  {/* BUY BUTTON */}
  <Button
    variant="contained"
    size="large"
    sx={{
      bgcolor: "#2B211C",
      color: "white",
      borderRadius: 2,
      px: 4,
      py: 1.2,
      textTransform: "none",
      fontWeight: 600,
      "&:hover": { bgcolor: "#333" },
      width: { xs: "100%", sm: "auto" }, // responsive
    }}
    onClick={() =>
      navigate("/service-request", {
        state: {
          service: {
            id: service.id,
            title: service.title,
            price: service.price,
            amount: service.amount,
            category: service.category,
            imageUrl: service.imageUrl,
          },
        },
      })
    }
  >
    Buy Now
  </Button>
</Card>

          </Box>

          {/* RIGHT: scrollable content */}
          <Box p={1.2}>
            {/* ABOUT */}
            {(service.about || service.longDescription) && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  border: "0px solid",
                  borderColor: "#DCD4CC",
                  backgroundColor: "transparent",
                }}
              >
                <Typography
                  variant="h5"
                  fontFamily="Georgia, Times New Roman, serif"
                  mb={1.5}
                  fontWeight={800}
                >
                  About This Service
                </Typography>
                <Typography sx={{ lineHeight: 1.8 }}>
                  {service.about || service.longDescription}
                </Typography>
              </Card>
            )}

            {/* FEATURES */}
            {Array.isArray(service.features) && service.features.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  border: "0px solid",
                  borderColor: "#DCD4CC",
                  backgroundColor: "transparent",
                }}
              >
                <Typography
                  variant="h5"
                  fontFamily="Georgia, Times New Roman, serif"
                  mb={2}
                >
                  Features Included
                </Typography>

                <Stack spacing={2}>
                  {service.features.map((feat, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "transparent",
                      }}
                    >
                      <Typography variant="h6" mb={0.5}>
                        - {feat.title || `Feature ${index + 1}`}
                      </Typography>
                      {feat.description && (
                        <Typography sx={{ lineHeight: 1.7, pl: 2 }}>
                          {feat.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Card>
            )}

            {/* DELIVERABLES */}
            {Array.isArray(service.deliverables) && service.deliverables.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  mt: 3,
                  mb: 3,
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #DCD4CC",
                  backgroundColor: "#FCFAF6",
                }}
              >
                <Typography
                  variant="h5"
                  fontFamily="Georgia, Times New Roman, serif"
                  mb={2}
                  fontWeight={700}
                >
                  Key Deliverables
                </Typography>
                <Grid container spacing={1.5}>
                  {service.deliverables.map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleOutlineRoundedIcon sx={{ color: "#A06E50", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            )}

            {/* FAQS */}
            {Array.isArray(service.faqs) && service.faqs.length > 0 && (
              <Box sx={{ mt: 4, mb: 3 }}>
                <Typography
                  variant="h5"
                  fontFamily="Georgia, Times New Roman, serif"
                  mb={2}
                  fontWeight={700}
                >
                  Frequently Asked Questions
                </Typography>
                {service.faqs.map((faq, idx) => (
                  <Accordion
                    key={idx}
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      border: "1px solid #DCD4CC",
                      borderRadius: "12px !important",
                      "&:before": { display: "none" },
                      backgroundColor: "#FCFAF6",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                        {faq.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}

            {/* WORK TOGETHER CTA */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                bgcolor: "#2B211C",
                color: "#F7F4EF",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#F7F4EF">
                  Have specific requirements for your business?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                  Speak with our engineering & design team in Jaipur to get a tailored estimate.
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/work-together"
                variant="contained"
                endIcon={<NorthEastRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: "#A06E50",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#8C5E43" },
                }}
              >
                Discuss Your Project
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ======= MORE SERVICES SECTIONS ======= */}
        <Box mt={10}>
          {/* More from same category */}
          {service.category && (
            <Box mb={5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h5" fontFamily="Georgia, Times New Roman, serif">
                  More services in&nbsp;
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {service.category}
                  </Box>
                </Typography>
              </Box>

              {moreLoading && sameCategoryServices.length === 0 ? (
                <Grid container spacing={2}>
                  {[1, 2, 3].map((n) => (
                    <Grid key={n} item xs={12} sm={6} md={4}>
                      <Skeleton
                        variant="rectangular"
                        height={160}
                        sx={{ borderRadius: 3 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : sameCategoryServices.length > 0 ? (
                <Grid container spacing={3}>
                  {sameCategoryServices.map((item) => (
                    <Grid key={item.id} item xs={12} sm={6} md={4}>
                      <Card
                        component={RouterLink}
                        to={`/services/${item.id}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          textDecoration: "none",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.03)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.2s ease",
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: 360,
                        }}
                      >
                        {item.imageUrl && (
                          <Box
                            component="img"
                            src={item.imageUrl}
                            alt={item.title}
                            sx={{
                              width: "100%",
                            height: 340,
                              objectFit: "cover",
                              borderRadius: 1,
                              mb: 1.5,
                            }}
                          />
                        )}
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.description || item.about || "View details"}
                        </Typography>
                        {item.price && (
                          <Typography sx={{ fontWeight: 600 }}>
                            ₹{item.price}
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No other services found in this category yet.
                </Typography>
              )}
            </Box>
          )}

          {/* More from us (different categories, no duplicates) */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5" fontFamily="Georgia, Times New Roman, serif">
                More services from us
              </Typography>
            </Box>

            {moreLoading && moreServices.length === 0 ? (
              <Grid container spacing={2}>
                {[1, 2, 3].map((n) => (
                  <Grid key={n} item xs={12} sm={6} md={4}>
                    <Skeleton
                      variant="rectangular"
                      height={160}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : moreServices.length > 0 ? (
              <Grid container spacing={3}>
                {moreServices.map((item) => (
                  <Grid key={item.id} item xs={12} sm={6} md={4}>
                    <Card
                      component={RouterLink}
                      to={`/services/${item.id}`}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        textDecoration: "none",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.03)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: 360,
                      }}
                    >
                      {item.imageUrl && (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.title}
                          sx={{
                            width: "100%",
                            height: 340,
                            objectFit: "cover",
                            borderRadius: 1,
                            mb: 1.5,
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {item.title}
                      </Typography>
                      {item.category && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {item.category}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.description || item.about || "View details"}
                      </Typography>
                      {item.price && (
                        <Typography sx={{ fontWeight: 600 }}>
                          ₹{item.price}
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more services available right now.
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ServiceDetailPage;
